/**
 * Générateur — Automatisme : multiplier un décimal par 1, 10, 100 ou 1 000
 * Attendu ID : 1008 | Niveau : 6ème | Domaine : NC
 * Rubrique : AUTOMATISME (programme 2025) — restitution en rituel court.
 * Labels (cartes exerciseur) :
 *   - Multiplier par 1, 10, 100 ou 1 000
 *
 * ── Plan de cartes (2026-08-20) ────────────────────────────────────────────
 * UNE carte, 5 formats rituels. « Multiplier par 10 ne consiste pas à
 * ajouter un zéro : chaque chiffre change de rang » (programme) — LE NOMBRE
 * GLISSE, LA VIRGULE RESTE FIXE. Tout le calcul est fait par GLISSEMENT DE
 * CHAÎNE (jamais de flottants) puis RE-VÉRIFIÉ numériquement (throw).
 * Opérande a : décimal E,D (E ∈ [0..12] comme 12,6 de la flash, D canonique
 * sans zéro final, zéros de tête possibles : 0,008 ; 5,03) ou ENTIER
 * (2..99 non multiple de 10) à raison de ~1/4 (arbitrage prof).
 * Multiplicateur m ∈ {1, 10, 100, 1000}. INVARIANTS :
 *   - chaque tirage multi-items contient EXACTEMENT UN « × 1 » (glissement
 *     nul : vérifie que l'élève n'applique pas la routine à l'aveugle),
 *     au plus un entier, au moins un a < 1 (traversée de la virgule :
 *     0,008 × 100 = 0,8 ; 0,09 × 1 000 = 90, les cas durs de la flash) ;
 *   - saisie : le trou est le RÉSULTAT, ou parfois le MULTIPLICATEUR
 *     (« 0,45 × {{100}} = 45 », solution unique par construction) — jamais
 *     l'opérande (ce serait une division : attendu suivant, frontière A5) ;
 *   - QCM : distracteurs = erreurs NOMMÉES (ajout_zero « 3,70 » : LA
 *     misconception du programme, fausse par VALEUR ; glissements ±1 ;
 *     sens_inverse ; routine_appliquee pour × 1), textuellement et
 *     numériquement distincts de la clé par construction ;
 *   - V/F : les lignes fausses re-vérifiées FAUSSES numériquement (un
 *     « × 1 » n'est jamais faussé par ajout_zero : 5,03 = 5,030 est VRAI) ;
 *   - affichage : milliers espacés (12 600), mots de trous en brut
 *     (« 12600 », la tolérance du scoring accepte les espaces) ;
 *   - texte BRUT partout (pas de LaTeX : aucune fraction en jeu).
 */
import {
    randomChoice, randInt, shuffle, formatNumber,
    makeQcm, makeVraiFaux, makeTexteTrou, makeClasser, filtrerDistracteurs,
} from '../../../utils';

const ATTENDU_ID = 1008;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L1 = 'Multiplier par 1, 10, 100 ou 1 000';

// ─── Nombres en chaînes (le glissement est un décalage de rang) ────────────

/** Un nombre = { ent: '3', dec: '7' } (dec vide pour un entier). */
const valeurDe = (n) => Number(`${n.ent}.${n.dec || '0'}`);

/** Écriture brute (mot de trou) : « 12600 », « 42,5 ». */
const brut = (n) => (n.dec ? `${n.ent},${n.dec}` : n.ent);

/** Écriture affichée : milliers espacés (« 12 600 », « 1 234,5 »). */
const affiche = (n) => (n.dec
    ? `${formatNumber(Number(n.ent))},${n.dec}`
    : formatNumber(Number(n.ent)));

/**
 * GLISSEMENT du nombre de k rangs vers la gauche (k > 0 : × 10^k) ou vers
 * la droite (k < 0), virgule fixe. Chaîne canonique en sortie (le dernier
 * chiffre décimal de l'entrée étant non nul, aucun zéro final ne naît).
 */
const glisser = (n, k) => {
    let digits = n.ent + n.dec;
    const newDec = n.dec.length - k;
    if (newDec <= 0) {
        const ent = (digits + '0'.repeat(-newDec)).replace(/^0+(?=\d)/, '');
        return { ent, dec: '' };
    }
    while (digits.length <= newDec) digits = `0${digits}`;
    return {
        ent: digits.slice(0, -newDec).replace(/^0+(?=\d)/, ''),
        dec: digits.slice(-newDec),
    };
};

/** AUTO-VÉRIFICATION : glissement et arithmétique doivent coïncider. */
const verifierProduit = (a, q) => {
    const res = glisser(a, q);
    const attendu = valeurDe(a) * Math.pow(10, q);
    if (Math.abs(valeurDe(res) - attendu) > attendu * 1e-12 + 1e-12) {
        throw new Error(`gen_1008 : glissement faux (${brut(a)} × 10^${q})`);
    }
    return res;
};

/** Tire un opérande décimal canonique : E ∈ [0..12], D sans zéro final. */
const tirerDecimal = () => {
    for (let t = 0; t < 100; t++) {
        const p = randInt(1, 3);
        const D = randInt(1, Math.pow(10, p) - 1);
        if (D % 10 === 0) continue;
        const E = randInt(0, 12);
        return { ent: String(E), dec: String(D).padStart(p, '0') };
    }
    return { ent: '3', dec: '7' };
};

/** Tire un opérande entier (2..99, non multiple de 10). */
const tirerEntier = () => {
    for (let t = 0; t < 100; t++) {
        const a = randInt(2, 99);
        if (a % 10 !== 0) return { ent: String(a), dec: '' };
    }
    return { ent: '45', dec: '' };
};

/** item = { a, q, trou } ; trou ∈ {'res','mult'} ; m = 10^q, q ∈ [0..3]. */
const M_AFFICHE = ['1', '10', '100', '1 000'];
const M_BRUT = ['1', '10', '100', '1000'];

const ligneItem = (item, slot = null) => {
    const { a, q, trou } = item;
    const res = verifierProduit(a, q);
    const mAff = M_AFFICHE[q];
    const marque = (v) => (slot === 'trou' ? `{{${v}}}` : slot === 'question' ? '…' : null);
    if (trou === 'mult' && slot !== null) {
        return `${affiche(a)} × ${marque(M_BRUT[q])} = ${affiche(res)}`;
    }
    if (slot !== null) {
        return `${affiche(a)} × ${mAff} = ${marque(brut(res))}`;
    }
    return `${affiche(a)} × ${mAff} = ${affiche(res)}`;
};

/**
 * Tire `nb` items : EXACTEMENT un « × 1 », au plus `maxEntiers` entiers,
 * au moins un a < 1, opérandes distincts (et mots distincts si demandé).
 * Boucle bornée ; repli déterministe conforme (les cas de la série flash).
 */
const tirerItems = (nb, { motsDistincts = false, maxEntiers = 1 } = {}) => {
    const motDe = (it) => (it.trou === 'mult' ? M_BRUT[it.q] : brut(glisser(it.a, it.q)));
    for (let essai = 0; essai < 80; essai++) {
        const pris = [];
        const vus = new Set();
        const mots = new Set();
        let entiers = 0;
        let avecUn = 0;
        for (let t = 0; t < 200 && pris.length < nb; t++) {
            const estEntier = entiers < maxEntiers && Math.random() < 0.25;
            const a = estEntier ? tirerEntier() : tirerDecimal();
            const q = avecUn === 0 && pris.length === nb - 1 ? 0
                : randomChoice(avecUn === 0 ? [0, 1, 2, 3] : [1, 2, 3]);
            const trou = q >= 1 && Math.random() < 0.25 ? 'mult' : 'res';
            const it = { a, q, trou };
            if (vus.has(brut(a))) continue;
            if (motsDistincts && mots.has(motDe(it))) continue;
            vus.add(brut(a));
            mots.add(motDe(it));
            if (estEntier) entiers++;
            if (q === 0) avecUn++;
            pris.push(it);
        }
        if (pris.length === nb
            && pris.filter((it) => it.q === 0).length === 1
            && pris.some((it) => it.a.ent === '0')) return shuffle(pris);
    }
    return [
        { a: { ent: '3', dec: '7' }, q: 1, trou: 'res' },     // 3,7 × 10
        { a: { ent: '0', dec: '008' }, q: 2, trou: 'res' },   // 0,008 × 100
        { a: { ent: '5', dec: '03' }, q: 0, trou: 'res' },    // 5,03 × 1
        { a: { ent: '0', dec: '45' }, q: 2, trou: 'mult' },   // 0,45 × ? = 45
    ].slice(0, nb);
};

// ─── Format 1 : texte à trous SAISIE (la série flash) ─────────────────────

const genProduitsTrouSaisie = () => {
    const items = tirerItems(4);
    const phrase = items.map((it) => ligneItem(it, 'trou')).join('\n');
    return makeTexteTrou(
        phrase,
        '<p><b>Complète chaque égalité.</b></p>',
        { ...opts(L1), mode: 'saisie' },
    );
};

// ─── Format 2 : texte à trous DRAG ────────────────────────────────────────

const genProduitsTrouDrag = () => {
    const items = tirerItems(3, { motsDistincts: true });
    const phrase = items.map((it) => ligneItem(it, 'trou')).join('\n');
    // Tuiles pièges : « ajout de zéro » d'un résultat décimal, glissement ±1,
    // multiplicateur voisin. filtrerDistracteurs écarte toute collision
    // numérique avec un mot attendu et les doublons entre pièges.
    // UN piège PAR LIGNE (arbitrage prof 2026-08-20 : aucune ligne sans
    // voisin piégeux). L'ajout de zéro porte sur l'OPÉRANDE (« 4,250 ») :
    // sur le résultat il vaudrait la clé. Dédoublonnage NUMÉRIQUE (« 4,25 »
    // et « 4,250 » passaient le filtre textuel ensemble, revue 2026-08-20).
    const candidatsDe = (it) => {
        if (it.trou === 'mult') {
            return [M_BRUT[it.q === 3 ? 2 : it.q + 1], M_BRUT[it.q === 1 ? 3 : it.q - 1]];
        }
        const c = [];
        if (it.a.dec && it.q >= 1) c.push(`${it.a.ent},${it.a.dec}0`);
        c.push(brut(glisser(it.a, it.q + 1)));                  // un rang de trop
        c.push(brut(glisser(it.a, it.q + 2)));
        if (it.q >= 1) c.push(brut(glisser(it.a, it.q - 1)));
        return c;
    };
    const distracteurs = [];
    const vusVal = new Set();
    for (const it of items) {
        const ok = filtrerDistracteurs(phrase, shuffle(candidatsDe(it)))
            .find((d) => !vusVal.has(Number(d.replace(',', '.'))));
        if (ok) {
            vusVal.add(Number(ok.replace(',', '.')));
            distracteurs.push(ok);
        }
    }
    return makeTexteTrou(
        phrase,
        '<p><b>Complète chaque égalité avec les bonnes étiquettes.</b></p>',
        { ...opts(L1), mode: 'drag', distracteurs },
    );
};

// ─── Format 3 : QCM (cf. REGLES_DISTRACTEURS_QCM) ─────────────────────────

const genProduitsQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const estEntier = Math.random() < 0.25;
        const a = estEntier ? tirerEntier() : tirerDecimal();
        const q = randomChoice([0, 1, 1, 2, 2, 3, 3]);          // × 1 à ~1/7
        const res = verifierProduit(a, q);
        const bonne = brut(res);

        let candidats;
        if (q === 0) {
            // × 1 : l'erreur est d'appliquer la routine quand même. JAMAIS
            // d'ajout_zero ici : « 5,030 » a la même valeur que la clé.
            candidats = [
                { valeur: brut(glisser(a, 1)), code: 'routine_appliquee' },
                { valeur: brut(glisser(a, -1)), code: 'routine_appliquee' },
                { valeur: brut(glisser(a, 2)), code: 'routine_appliquee' },
            ];
        } else {
            candidats = [
                // ajout_zero : même VALEUR que a, donc jamais égal à la clé
                // (q ≥ 1) — réservé aux décimaux (pour un entier, ajouter un
                // zéro donne le bon résultat de × 10).
                a.dec ? { valeur: `${a.ent},${a.dec}0`, code: 'ajout_zero' }
                    : { valeur: brut(glisser(a, -q)), code: 'sens_inverse' },
                // entier avec q = 1 : le 2e candidat sens_inverse dupliquait le
                // 1er (mesuré : les entiers tombaient à 19 % au lieu de 25 %,
                // revue 2026-08-20) → glissement inverse de DEUX rangs.
                q >= 2 ? { valeur: brut(glisser(a, q - 1)), code: 'glissement_insuffisant' }
                    : a.dec ? { valeur: brut(glisser(a, -q)), code: 'sens_inverse' }
                        : { valeur: brut(glisser(a, -q - 1)), code: 'sens_inverse' },
                { valeur: brut(glisser(a, q + 1)), code: 'glissement_excessif' },
            ];
        }
        // Affichage des choix : milliers espacés (« 76 090 »), comme partout.
        const affStr = (s) => (s.includes(',') ? s : formatNumber(Number(s)));
        const choix = [bonne, ...candidats.map((c) => c.valeur)].map(affStr);
        if (new Set(choix).size !== 4) continue;
        // Garantie A1 : aucun distracteur n'a la valeur de la clé.
        if (candidats.some((c) => Math.abs(
            Number(c.valeur.replace(',', '.')) - valeurDe(res),
        ) < 1e-12)) continue;

        const texte = `<p><b>Combien vaut ${affiche(a)} × ${M_AFFICHE[q]} ?</b></p>`;
        const payload = makeQcm(choix, choix[0], texte, opts(L1), false);
        payload._meta.params = { a: brut(a), q, distracteurs: candidats };
        return payload;
    }
    // Repli déterministe : le cas emblématique de la flash.
    const payload = makeQcm(
        ['37', '3,70', '0,37', '370'],
        '37',
        '<p><b>Combien vaut 3,7 × 10 ?</b></p>',
        opts(L1),
        false,
    );
    payload._meta.params = { a: '3,7', q: 1, distracteurs: [] };
    return payload;
};

// ─── Format 4 : tableau Vrai/Faux ─────────────────────────────────────────

/**
 * Ligne fausse = résultat remplacé par une erreur NOMMÉE, re-vérifiée
 * FAUSSE numériquement (throw sinon). Un « × 1 » n'est faussé QUE par
 * glissement (« 5,03 × 1 = 50,3 ») : « 5,030 » serait VRAI.
 */
const fausserItem = (item) => {
    const { a, q } = item;
    const res = glisser(a, q);
    const candidats = [];
    if (q >= 1 && a.dec) candidats.push(`${a.ent},${a.dec}0`);   // ajout de zéro
    candidats.push(brut(glisser(a, q + 1)));
    if (q >= 1) candidats.push(brut(glisser(a, q - 1)));
    else candidats.push(brut(glisser(a, -1)));
    const faux = randomChoice(candidats);
    if (Math.abs(Number(faux.replace(',', '.')) - valeurDe(res)) < 1e-12) {
        throw new Error('gen_1008 : la ligne censée être fausse est vraie');
    }
    const fauxAff = faux.includes(',') ? faux : formatNumber(Number(faux));
    return `${affiche(a)} × ${M_AFFICHE[q]} = ${fauxAff}`;
};

const genProduitsVraiFaux = () => {
    let lignes = [];
    let etiquettes = [];
    for (let essai = 0; essai < 30; essai++) {
        const items = tirerItems(4);
        const nbVraies = randInt(1, 3);
        const etiq = shuffle([
            ...Array(nbVraies).fill(0),
            ...Array(4 - nbVraies).fill(1),
        ]);
        const ls = items.map((it, i) => (
            etiq[i] === 0 ? ligneItem(it) : fausserItem(it)
        ));
        lignes = ls;
        etiquettes = etiq;
        if (new Set(ls).size === 4) break;
    }
    return makeVraiFaux(
        lignes,
        etiquettes,
        '<p><b>Vrai ou faux ?</b></p>',
        opts(L1),
    );
};

// ─── Format 5 : classer ───────────────────────────────────────────────────

/**
 * Catégories = les résultats « mêmes chiffres, glissements différents »
 * (4,5 / 45 / 450) ; items = des produits qui les atteignent par des
 * chemins différents (0,45 × 100 ; 4,5 × 10 ; 0,045 × 1 000...).
 * Valeurs des catégories distinctes par construction (× 10 entre elles)
 * → catégorie UNIQUE par item.
 */
const genProduitsClasser = () => {
    for (let essai = 0; essai < 60; essai++) {
        const base = tirerDecimal();
        if (!base.dec || base.ent === '0') continue;             // ex. 4,5
        const cibles = shuffle([0, 1, 2]).slice(0, randInt(2, 3));
        let horsDomaine = false;
        const categories = cibles.map((k) => {
            const cible = glisser(base, k);
            // Les opérandes doivent RESTER dans le domaine de la flash :
            // ≤ 3 décimales (pas de « 0,006322 × 1 000 ») ET canoniques
            // (une cible finissant par zéro, comme 690, produirait
            // « 6,90 × 100 » au glissement inverse — revue 2026-08-20).
            // q = 0 est admis (« 3,57 × 1 », l'anti-routine dans le classer) :
            // sans lui, la 3e catégorie du même motif était structurellement
            // impossible (mesuré 5/5000 tirages à 3 catégories).
            const qsValides = [0, 1, 2, 3].filter((q) => {
                const a = glisser(cible, -q);
                return a.dec.length <= 3 && !a.dec.endsWith('0');
            });
            if (qsValides.length < 2) { horsDomaine = true; return null; }
            const qs = shuffle(qsValides).slice(0, 2);
            const items = qs.map((q) => {
                const a = glisser(cible, -q);
                verifierProduit(a, q);
                return `${affiche(a)} × ${M_AFFICHE[q]}`;
            });
            return { name: affiche(cible), items };
        });
        if (horsDomaine) continue;
        const tous = categories.flatMap((c) => c.items);
        if (new Set(tous).size !== tous.length) continue;
        return makeClasser(
            categories,
            '<p><b>Classe chaque produit selon son résultat.</b></p>',
            opts(L1),
        );
    }
    return makeClasser(
        [
            { name: '4,5', items: ['0,45 × 10', '0,045 × 100'] },
            { name: '45', items: ['4,5 × 10', '0,45 × 100'] },
            { name: '450', items: ['45 × 10', '0,45 × 1 000'] },
        ],
        '<p><b>Classe chaque produit selon son résultat.</b></p>',
        opts(L1),
    );
};

// ─── Contrat distracteurs (référence de revue, format QCM) ────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'chaque distracteur est une erreur NOMMÉE, textuellement distincte (garde Set) ET de valeur différente de la clé (garde numérique, re-tirage sinon)',
        'le calcul est un GLISSEMENT DE CHAÎNE (le nombre glisse, la virgule reste fixe), jamais un flottant',
    ],
    produits_qcm_unique: {
        support: 'un produit a × m (a décimal, ou entier ~1/4 ; m ∈ {1, 10, 100, 1000})',
        fixes: {
            ajout_zero: 'LA misconception du programme : « 3,7 × 10 = 3,70 » — un zéro collé au lieu d\'un glissement, la valeur ne change pas (réservée aux décimaux : pour un entier, ajouter un zéro donne le bon résultat)',
            glissement_insuffisant: 'un rang de moins que nécessaire (0,45 × 100 = 4,5)',
            glissement_excessif: 'un rang de trop (3,7 × 10 = 370)',
            sens_inverse: 'le nombre glisse du mauvais côté (3,7 × 10 = 0,37)',
            routine_appliquee: '× 1 : l\'élève glisse quand même (5,03 × 1 = 50,3 ou 0,503) — le cas anti-routine de la série flash',
        },
    },
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    produits_texte_trou_saisie: genProduitsTrouSaisie,
    produits_texte_trou_drag: genProduitsTrouDrag,
    produits_qcm_unique: genProduitsQcm,
    produits_tableau_vf: genProduitsVraiFaux,
    produits_classer: genProduitsClasser,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    tableau_vf: 'Vrai/Faux', classer: 'Classer',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'produits', label: L1, formatsList: buildFormats('produits') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
